/**
 * Login Component — Nordic Editorial Split-Screen
 * Displays login UI and handles authentication flow
 *
 * ═══════════════════════════════════════════════════════════════════════════
 * CAROUSEL IMAGE SPECIFICATIONS
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Resolution:
 *   - Minimum:  1920 × 1280 px
 *   - Ideal:    2400 × 1600 px (Retina/4K ready)
 *
 * Aspect Ratio: ~3:2 or 16:10
 * Format:       WebP or optimized JPG
 * File Size:    < 200KB per image (compressed)
 *
 * Video (optional):
 *   - Resolution: 1920 × 1080 (1080p) or 1280 × 720
 *   - Format:     MP4 (H.264)
 *   - Duration:   10–15 seconds, looping
 *   - File Size:  < 2–3 MB
 *
 * Usage: Add images to src/assets/login/ and import them below.
 * ═══════════════════════════════════════════════════════════════════════════
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import logo from '../assets/cc-studio-logo.png';

// ─── Carousel Images ───────────────────────────────────────────────────────
// Import your images here and add them to the carouselSlides array below.
// Example:
//   import slide1 from '../assets/login/slide1.webp';
//   import slide2 from '../assets/login/slide2.webp';
// Then set: image: slide1 in the slide object
// ────────────────────────────────────────────────────────────────────────────

// Rotating taglines for the subheader
const taglines = [
  'Your creative studio awaits.',
  'Sign in to start creating.',
  'Where ideas become visuals.',
  'Create, iterate, deliver.',
  'Design at the speed of thought.',
  'Your workspace is ready.',
];

// Carousel slide type
interface CarouselSlide {
  id: number;
  image?: string;              // Imported image or URL
  background?: string;         // Fallback gradient if no image
  headline: string;
  subline: string;
}

// Placeholder carousel slides — replace gradients with images when ready
const carouselSlides: CarouselSlide[] = [
  {
    id: 1,
    // image: slide1,          // ← Uncomment and set when image is ready
    background: 'linear-gradient(135deg, #2E2B27 0%, #4A4540 50%, #6F6A63 100%)',
    headline: 'Create with AI',
    subline: 'Transform your ideas into stunning visuals',
  },
  {
    id: 2,
    // image: slide2,          // ← Uncomment and set when image is ready
    background: 'linear-gradient(145deg, #3F5B4B 0%, #6A9B8A 100%)',
    headline: 'Design at Scale',
    subline: 'Generate hundreds of variations in minutes',
  },
  {
    id: 3,
    // image: slide3,          // ← Uncomment and set when image is ready
    background: 'linear-gradient(160deg, #4A6B7A 0%, #36505C 50%, #2E2B27 100%)',
    headline: 'Your Brand, Elevated',
    subline: 'AI-powered creativity for modern teams',
  },
];

const Login: React.FC = () => {
  const { login, isLoading, error } = useAuth();
  const [currentSlide, setCurrentSlide] = useState(0);

  // Random start index for taglines (computed once on mount)
  const [currentTagline, setCurrentTagline] = useState(() =>
    Math.floor(Math.random() * taglines.length)
  );
  const [taglineVisible, setTaglineVisible] = useState(true);

  // Auto-advance carousel
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % carouselSlides.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  // Auto-cycle taglines with fade animation
  useEffect(() => {
    const timer = setInterval(() => {
      // Fade out
      setTaglineVisible(false);

      // After fade out completes, change text and fade in
      setTimeout(() => {
        setCurrentTagline((prev) => (prev + 1) % taglines.length);
        setTaglineVisible(true);
      }, 300); // Match the CSS transition duration
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* ─── Left: Media Carousel ─── */}
      <div className="relative w-full lg:w-[55%] h-[40vh] lg:h-screen overflow-hidden">
        {/* Slides */}
        {carouselSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
              index === currentSlide ? 'opacity-100' : 'opacity-0'
            }`}
            style={!slide.image ? { background: slide.background } : undefined}
          >
            {/* Background image (if provided) */}
            {slide.image && (
              <img
                src={slide.image}
                alt={slide.headline}
                className="absolute inset-0 w-full h-full object-cover"
              />
            )}
            {/* Content overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-12 left-8 right-8 lg:bottom-16 lg:left-12 lg:right-12 text-white">
              <h2 className="font-display text-3xl lg:text-4xl tracking-tight mb-2">
                {slide.headline}
              </h2>
              <p className="text-body text-white/80">{slide.subline}</p>
            </div>
          </div>
        ))}

        {/* Carousel indicators */}
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
          {carouselSlides.map((_, index) => (
            <button
              type="button"
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentSlide
                  ? 'bg-white w-6'
                  : 'bg-white/40 hover:bg-white/60'
              }`}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>

      {/* ─── Right: Login Form ─── */}
      <div className="flex-1 bg-surface-app flex flex-col p-8 lg:p-12">
        {/* Logo — top right */}
        <div className="flex justify-end mb-auto">
          <img
            src={logo}
            alt="CC Studio"
            className="h-8 lg:h-10 w-auto"
          />
        </div>

        {/* Centered content */}
        <div className="flex-1 flex items-center justify-center">
          <div className="w-full max-w-sm">
            {/* Heading */}
            <h1 className="font-display text-3xl lg:text-4xl text-gray-900 tracking-tight mb-3">
              Welcome to CC Studio
            </h1>
            <p
              className={`text-body text-gray-600 mb-10 transition-all duration-300 ease-in-out ${
                taglineVisible
                  ? 'opacity-100 translate-y-0'
                  : 'opacity-0 -translate-y-1'
              }`}
            >
              {taglines[currentTagline]}
            </p>

            {/* Sign in button — black per Nordic Editorial */}
            <button
              type="button"
              onClick={login}
              disabled={isLoading}
              className="w-full py-4 px-6 bg-blue-500 hover:bg-blue-600 active:bg-blue-700
                         text-white text-body font-semibold rounded-md shadow-generate
                         hover:shadow-xl transition-all duration-base
                         disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing in…' : 'Sign in with Microsoft'}
            </button>

            {/* Error message */}
            {error && (
              <div className="mt-4 px-4 py-3 bg-error-light text-error border border-error/30 rounded-md text-sm">
                {error}
              </div>
            )}
          </div>
        </div>

        {/* Footer — bottom */}
        <div className="mt-auto pt-8">
          <p className="text-meta text-gray-500 text-center">
            By Creatives Combined
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
